// src/lib/walkthrough/actionClassify.ts
//
// PURE walkthrough step-classification + advance-decision logic.
// Extracted out of WalkthroughPlayer.ts (which is DOM/React coupled and cannot
// run under the node-env vitest gate) so these decisions are unit-testable and
// guarded against regression. WalkthroughPlayer imports these; the player owns
// the DOM wiring (listeners, popover), these helpers own the DECISIONS.
//
// Regression guards (docs/REGRESSIONS.md 2026-07-01):
//  - a "Set …" field step must classify as kind 'set' (not 'other') so the
//    player can auto-advance it — a step that dead-ends is a defect.
//  - every step must have a manual advance path (shouldAllowManualAdvance).

import type { KbStep } from '../../views/knowledge-base/types'

export type ActionKind = 'click' | 'type' | 'navigate' | 'set' | 'other'

// Leading action verb → step kind. `Set|Update|Change|Pick|Toggle` are the
// FIELD-SET verbs: the user commits a value into a form field (date picker,
// toggle, select) and the step advances when that field's value changes.
// Without these here, "Set the project Start date" fell through to kind 'other'
// which had NO auto-advance listener AND (previously) no manual fallback →
// permanent dead-end on step 5/14. Keep in lockstep with deriveAction below.
export const ACTION_RE =
  /^\s*(Click|Type|Navigate|Press|Select|Choose|Hover|Drag|Open|Close|Enter|Submit|Set|Update|Change|Pick|Toggle)\b/i

export function deriveAction(step: KbStep): { label: string; kind: ActionKind } {
  const m = step.text.match(ACTION_RE)
  if (m) {
    const word = m[1]
    const lower = word.toLowerCase()
    let kind: ActionKind = 'other'
    if (lower === 'click' || lower === 'press' || lower === 'select' || lower === 'choose' || lower === 'hover' || lower === 'open' || lower === 'close') kind = 'click'
    else if (lower === 'type' || lower === 'enter' || lower === 'submit') kind = 'type'
    else if (lower === 'navigate') kind = 'navigate'
    else if (lower === 'set' || lower === 'update' || lower === 'change' || lower === 'pick' || lower === 'toggle') kind = 'set'
    return { label: word, kind }
  }
  const tag = (step.tag || '').toUpperCase()
  if (tag === 'INPUT' || tag === 'TEXTAREA') return { label: 'Type', kind: 'type' }
  if (tag === 'BUTTON' || tag === 'A' || tag === 'SVG') return { label: 'Click', kind: 'click' }
  return { label: 'Action', kind: 'other' }
}

// Whether the popover should render a manual "Continue" button for this step.
//
// GENERAL DEAD-END GUARD: a user must never be stuck on a step with no path
// forward. Rules:
//  - Element did NOT resolve (foundEl=false): ALWAYS offer Continue — the user
//    may be unable to perform the action to auto-advance, for ANY kind. (This
//    replaces the old `kind === 'click'`-only manual fallback.)
//  - Element resolved (foundEl=true): 'click'/'type' auto-advance when the user
//    performs the action, so we preserve their forcing function (no skip). Every
//    OTHER kind ('set'/'navigate'/'other') has no guaranteed user-action
//    listener, so it MUST offer a manual Continue.
export function shouldAllowManualAdvance(kind: ActionKind, foundEl: boolean): boolean {
  if (!foundEl) return true
  return kind !== 'click' && kind !== 'type'
}

// Whether a selectable ROW's selection-state attributes represent a TRUTHY
// (selected/checked) state. Used by the player's 'click'/select advance path for
// custom DIV-checkbox rows (e.g. the project-owner option row) whose selection is
// a React onClick that flips `data-selected`/`aria-checked` — NOT a native
// input/change. The player observes the row's attributes and calls this to decide
// when the owner became selected, so the "Select … owner" step auto-advances even
// though the click lands on the checkbox/row (not the resolved name-span leaf).
//
// Regression guard (docs/REGRESSIONS.md 2026-07-01): the "Select the owner" step
// dead-ended because clicking the row (custom checkbox) fired no input/change and
// the click-match only covered the resolved leaf span. Detect the selection state.
export function selectionIsTruthy(
  dataSelected: string | null | undefined,
  ariaChecked: string | null | undefined,
): boolean {
  const ds = (dataSelected || '').trim().toLowerCase()
  if (ds === '1' || ds === 'true' || ds === 'yes' || ds === 'selected' || ds === 'checked') return true
  const ac = (ariaChecked || '').trim().toLowerCase()
  return ac === 'true'
}

const normalizeValue = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim()

// Decision for a field-set ('set') step's input/change listener. The player
// supplies DOM-derived booleans/strings; this stays pure.
//  - isTrusted: reject synthetic events (our own value-clearing, autofill, etc.)
//  - withinIgnoreWindow: reject in-flight events bleeding across step changes.
//  - targetInsideStepTarget: the changed field must be inside the step's target.
//  - expectedValue empty  → advance on ANY value change.
//  - expectedValue present → advance only when the new value contains it.
export function fieldSetShouldAdvance(params: {
  isTrusted: boolean
  withinIgnoreWindow: boolean
  targetInsideStepTarget: boolean
  currentValue: string
  expectedValue: string
}): boolean {
  if (!params.isTrusted) return false
  if (params.withinIgnoreWindow) return false
  if (!params.targetInsideStepTarget) return false
  const expected = normalizeValue(params.expectedValue || '')
  if (!expected) return true
  return normalizeValue(params.currentValue || '').includes(expected)
}
